<template>
    <div>
        <b-form novalidate>
            <div class="wrapper">
                <div class="left">
                    <b-form-group>
                        <label><strong>{{ $t('project.settings.general.queryDepth') }}</strong></label>
                        <b-form-input name="queryDepth"
                            type="number"
                            v-validate="'required|numeric|min_value:1|max_value:10'"
                            min="1"
                            max="10"
                            v-model="projectData.documentQueryDepth"
                            :disabled="!projectPermissions.includes('CHANGE_FILE_TREE_DEPTH')"
                            :state="!veeErrors.has('queryDepth')">
                        </b-form-input>
                        <span v-show="veeErrors.has('queryDepth')" style="color: red;" class="small">{{ $t('project.settings.advanced.validation.queryDepth') }}</span>
                    </b-form-group>
                    <hr>
                    <b-button :disabled="veeErrors.has('queryDepth')" type="button" class="btn-primary" @click="saveSettings()" v-show="projectPermissions.includes('CHANGE_FILE_TREE_DEPTH')">{{ $t('project.settings.apply') }}</b-button>
                    <hr v-show="isProjectOwner">
                    <label v-show="isProjectOwner"><strong>{{ $t('project.settings.advanced.exportProjectRoomContents') }}</strong></label>
                    <b-form-group v-show="isProjectOwner">
                        <b-form-checkbox v-model="includeMessages">
                            <span class="line-h-25" v-text="$t('project.settings.advanced.includeMessages')"></span>
                        </b-form-checkbox>
                        <b-form-checkbox v-model="includeTasks">
                            <span class="line-h-25" v-text="$t('project.settings.advanced.includeTasks')"></span>
                        </b-form-checkbox>
                        <b-form-checkbox v-model="includeMembers">
                            <span class="line-h-25" v-text="$t('project.settings.advanced.includeMembers')"></span>
                        </b-form-checkbox>
                    </b-form-group>
                    <hr v-show="isProjectOwner">
                    <b-form-group>
                        <b-button :disabled="exportDisabled" type="button" class="btn-primary" @click="openExportDialog()" v-show="isProjectOwner">{{ $t('project.settings.advanced.downloadPDF') }}</b-button>
                    </b-form-group>
                </div>
                <div class="right">
                    <b-button type="button" size="lg" class="btn-delete" variant="danger" @click="deleteProjectRoom()" :disabled="!projectPermissions.includes('DELETE_PROJECT_ROOM')">
                        <span>{{ $t('project.settings.advanced.delete') }}</span>
                    </b-button>
                </div>
            </div>
        </b-form>
        <vue-html2pdf
                :show-layout="false"
                :enable-download="true"
                :preview-modal="false"
                :paginate-elements-by-height="1700"
                :pdf-quality="2"
                :manual-pagination="false"
                :filename=projectData.name
                pdf-format="a4"
                pdf-orientation="landscape"
                pdf-content-width="1130px"
                ref="html2Pdf"
                @hasStartedGeneration="hasStartedGeneration()"
                @hasGenerated="hasGenerated($event)"
        >
            <section slot="pdf-content">
                <export-contents-dialog :exportData="exportData" slot="pdf-content"></export-contents-dialog>
            </section>
        </vue-html2pdf>
    </div>
</template>

<script>
import toastr from 'toastr';
import {mapGetters} from 'vuex';
import i18n from '@/i18n.bootstrap';
import projectsApi from '../../../api/projects';
import ExportContentsDialog from "./ExportContentsDialog";
import VueHtml2pdf from 'vue-html2pdf';

export default {
    name: 'tab-advanced',
    components: {ExportContentsDialog, VueHtml2pdf},
    data() {
        return {
            includeMembers: true,
            includeMessages: true,
            includeTasks: true,
            exportDisabled: false,
            exportData: null,
        }
    },

    computed: {
        ...mapGetters({
            isProjectOwner: 'projects/isProjectOwner',
            projectData: 'projects/projectData'
        }),
        projectPermissions() {
          if(this.$store.state.projects.currentProject.projectData.role){
            return this.$store.state.projects.currentProject.projectData.role.permissions;
          } else {
            return [];
          }
        },
    },

    methods: {
        generateReport () {
            this.$refs.html2Pdf.generatePdf()
        },
        hasGenerated(blobPdf) {
            toastr.success(i18n.t('project.settings.files.downloadSuccess'));
            this.exportDisabled = false;
        },
        openExportDialog () {
            let projectId = this.$route.params.projectId;
            this.exportDisabled = true;
            toastr.info(i18n.t('project.settings.files.downloadInProgress'));
            projectsApi.postExportSettings(projectId, {showMembers: this.includeMembers, showMessages: this.includeMessages, showTasks: this.includeTasks})
                .then(response => {
                    if(response) {
                        this.exportData = response.data;
                        this.generateReport();
                    }
                })
                .catch(() => toastr.error(i18n.t('project.settings.files.errorDownloadingFiles')));
        },

        saveSettings() {
            let projectId = this.$route.params.projectId;
            this.$validator.validateAll().then(result => {
                if (result) {
                    this.$store.dispatch('projects/updateProject', this.projectData)
                        .then(() => {
                            this.$store.dispatch('projects/loadFilesByProject', { projectId })
                        });
                }
            });
        },

        deleteProjectRoom() {
            let { projectId } = this.$route.params;
            const deleteProjectRoomAction = () => {
                return projectsApi.deleteProject(projectId)
                        .then((res) => {
                            if(res) {
                                this.$router.push({ name: 'dashboard' });
                                toastr.success(i18n.t('project.action.successDelete', {projectName: this.projectData.name}));
                            }
                        })
                        .catch(() => toastr.error(i18n.t('project.action.errorDelete', {projectName: this.projectData.name})));
            };

            this.$snotify.confirm(i18n.t('project.settings.advanced.deleteMessage'), '', {
                closeOnClick: true,
                backdrop: 0.3,
                buttons: [
                    { text: i18n.t('general.yes'), action: deleteProjectRoomAction },
                    { text: i18n.t('general.no') }
                ]
            });
        }
    }
}
</script>

<style scoped>
.wrapper {
    display: flex;
}

.left {
    flex: 0 0 50%;
    padding: 3%;
    border-right: 1px solid #dddddd;
}

.right {
    flex: 1;
    padding: 3%;
    display: flex;
    justify-content: center;
    align-self: center;
}
</style>
